import { Injectable, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { fromEvent } from 'rxjs';
import { WpfService } from '../connectivity/wpf.service';
import { selectSeReportCurrentExaminationIds } from '../report/report.selectors';
import { initialRootState } from '../root-store/root.state';
import { ServerRootDetector } from '../utils/routing/server-root-detector';
import { InMemoryTokenStore } from '../utils/token-interceptor/inmemory-tokenstore.service';
import { jsonBlob } from '../utils/utils';
import { AuthenticationService } from './authentication/authentication.service';
import { ChildWindowBuilderService, WindowTarget } from './embedding/child.service';
import { TpIntegrationsCacheService } from './tp-integrations/tp-integrations-cache.service';

export type OnUnloadEventHandler = (event: BeforeUnloadEvent) => void; // se ritorna true allora visualizzo il messaggio di alert.

@Injectable({
    providedIn: 'root',
})
export class PageUnloadService {
    private readonly authenticationService = inject(AuthenticationService);
    private readonly store = inject(Store);
    private readonly tokenStore = inject(InMemoryTokenStore);
    private readonly tpIntegrationsCache = inject(TpIntegrationsCacheService);
    private readonly wpf = inject(WpfService);
    private readonly childWindowService = inject(ChildWindowBuilderService);

    private callbacks: OnUnloadEventHandler[] = [];
    private isForced = false;

    // Caso particolare in cui lo store dev'essere sincrono: ci serve all'unload della pagina,
    //   non possiamo aspettare un timeout perché la pagina verrebbe chiusa.
    private currentExaminationIds = initialRootState.report.currentExaminationIds;

    constructor() {
        fromEvent(window, 'beforeunload').subscribe((event) => this.onBeforeUnload(event));
        // l'evento unload è deprecato e viene sostituito con pagehide
        fromEvent(window, 'pagehide').subscribe((event) => this.onPageHide(event));

        this.store
            .pipe(select(selectSeReportCurrentExaminationIds))
            .subscribe((ids) => (this.currentExaminationIds = ids));
    }

    setIsForced(isForced: boolean) {
        this.isForced = isForced;
    }

    register(cb: OnUnloadEventHandler) {
        this.callbacks.push(cb);
    }

    unregister(cb: OnUnloadEventHandler) {
        this.callbacks = this.callbacks.filter((callback) => callback != cb);
    }

    private onBeforeUnload(event: BeforeUnloadEvent) {
        /**  chiudiamo tutte le finestre aperte. */
        this.childWindowService.closeAll();
        /** se non è una chiusura forzata (redirect alla pagina di login non facciamo altro) chiamo tutte le callback registrate */
        if (!this.isForced) {
            for (const callback of this.callbacks) {
                callback(event);
            }
        }
    }

    private onPageHide = (event: any) => {
        /** chiudiamo il wpf se aperto */
        if (this.authenticationService.isOpenedFromWpf) {
            this.wpf.webClientClosing?.('onUnload');
        }
        /** inviamo il messaggio di logout al server */
        navigator.sendBeacon(
            ServerRootDetector.getInstance().getResourceRoot() + '/Authentication/PageUnload',
            jsonBlob({
                // Salta il logout se aperto da WPF o da un altro tab.
                logout: !this.authenticationService.hasSecondaryAuthentication,
                token: this.tokenStore.getToken(),
                ECG_Unlock_Examinations: this.currentExaminationIds.ecg,
                Visit_Unlock_Examinations: this.currentExaminationIds.visit,
                oidcToken: (this.tpIntegrationsCache.get('sso-token') as any)?.id_token,
            }),
        );
        /** test */
        /** navigator.sendBeacon('http://localhost:43210/unload/unload');*/
    };
}
